import { Phone, MessageCircle, Smartphone } from "lucide-react";

const ICONS = { call: Phone, whatsapp: MessageCircle, sms: Smartphone };
const LABELS = { call: "Call", whatsapp: "WA", sms: "SMS" };

/**
 * AttemptCounter — visual badge showing "made/required" per channel.
 *
 * Variants:
 *   "compact" — for table cells / mobile cards (tiny pills)
 *   "full"    — for lead drawer (vertical list with progress)
 *
 * Behind = red dot. On track = green dot. Fresh (no expectations yet) = slate.
 */
export default function AttemptCounter({ status, variant = "compact" }) {
  if (!status) return null;
  const { required = {}, made = {}, status: s = "fresh" } = status;
  const dotColor =
    s === "fresh"
      ? "bg-slate-300"
      : s === "on_track"
        ? "bg-emerald-500"
        : s === "slight"
          ? "bg-amber-500"
          : "bg-rose-500";

  const channels = ["call", "whatsapp", "sms"].filter(
    (c) => (required[c] || 0) > 0 || (made[c] || 0) > 0,
  );
  if (!channels.length) {
    return (
      <span
        className="inline-flex items-center gap-1 text-[10px] text-slate-400"
        data-testid="attempt-counter"
        data-status={s}
      >
        <span className={`h-1.5 w-1.5 rounded-full ${dotColor}`} />
        Fresh
      </span>
    );
  }

  if (variant === "compact") {
    return (
      <span
        className="inline-flex items-center gap-1.5"
        data-testid="attempt-counter"
        data-status={s}
        title={`SLA: ${s}`}
      >
        <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${dotColor}`} />
        {channels.map((c) => {
          const Icon = ICONS[c];
          const m = made[c] || 0;
          const r = required[c] || 0;
          const overdue = m < r;
          return (
            <span
              key={c}
              className={`inline-flex items-center gap-0.5 rounded px-1 py-0.5 text-[10px] font-mono ${
                overdue
                  ? "bg-rose-50 text-rose-700"
                  : "bg-emerald-50 text-emerald-700"
              }`}
            >
              <Icon className="h-2.5 w-2.5" />
              {m}/{r}
            </span>
          );
        })}
      </span>
    );
  }

  return (
    <div
      className="space-y-1.5"
      data-testid="attempt-counter"
      data-status={s}
    >
      {channels.map((c) => {
        const Icon = ICONS[c];
        const m = made[c] || 0;
        const r = required[c] || 0;
        const pct = r > 0 ? Math.min(100, Math.round((100 * m) / r)) : 100;
        const overdue = m < r;
        return (
          <div key={c} className="flex items-center gap-2 text-xs">
            <Icon
              className={`h-3.5 w-3.5 ${
                overdue ? "text-rose-500" : "text-emerald-600"
              }`}
            />
            <span className="w-12 text-slate-600">{LABELS[c]}</span>
            <div className="relative h-1.5 flex-1 rounded-full bg-slate-100 overflow-hidden">
              <div
                className={`absolute inset-y-0 left-0 ${
                  overdue ? "bg-rose-400" : "bg-emerald-500"
                }`}
                style={{ width: `${pct}%` }}
              />
            </div>
            <span
              className={`font-mono text-[11px] ${
                overdue ? "text-rose-700" : "text-emerald-700"
              }`}
            >
              {m}/{r}
            </span>
          </div>
        );
      })}
    </div>
  );
}
